import { TimeUnit, MetricComparisonOperatorToString, ClientWorkflow } from "@avenueops/shared-types";
import { MetricAggregationFunction, MetricComparisonOperator } from "@prisma/client";
import PopoverTooltip from "components/generic-building-blocks/PopoverTooltip";
import InputCreatableSelect from "components/inputs/InputCreatableSelect";
import InputField from "components/inputs/InputField";
import InputSearchableSelect from "components/inputs/InputSearchableSelect";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

const defaultMetricPercentiles = [50, 75, 90, 95, 99];
const defaultMetricRunsToHealthy = 3;

interface Props {
  columnNames: string[];
  monitor: ClientWorkflow | undefined;
}

// leading-[3rem] to handle line spacing, -mt-2 to remove the extra line height for the top line

export default function ConfigureMetricMonitor({ columnNames, monitor }: Props): JSX.Element {
  const { register, control, formState, watch, setValue } = useFormContext();
  const { errors } = formState;
  const metricRunsToHealthy = watch("metricRunsToHealthy");
  // Using a state hook here instead of a watch because was running into some weirdness where the MetricSelector wasn't rendering
  // at first when the default value should have been "STANDALONE". A state hook ensures the default value is set before RadioGroupList
  // renders.

  const metricAggregationFunction = useWatch({ name: "metricAggregationFunction" }) as MetricAggregationFunction;

  return (
    <>
      <div className="flex flex-row mt-9">
        <div className=" flex-1">
          <div className="text-gray-900 font-medium gap-2 flex flex-row items-center">
            <div>Set metric health</div>
            <PopoverTooltip>
              <div className="p-2 w-64">
                {`See the `}
                <a href={"https://docs.avenue.so/monitors/metric-monitors#configure-your-alerting-conditions"}>
                  <span className="text-gray-800 underline">metric monitor docs</span>
                </a>
                {` for more information on metric monitor health.`}
              </div>
            </PopoverTooltip>
          </div>
          <div className="text-sm text-gray-600 font-normal">
            {`Consider a metric healthy, after ${metricRunsToHealthy} healthy run${metricRunsToHealthy === "1" ? "" : "s"}.`}
          </div>
        </div>
        <div className=" flex-1">
          <InputField
            hideErrorMessage
            errors={errors}
            placeholder="e.g. 3"
            defaultValue={defaultMetricRunsToHealthy}
            register={register("metricRunsToHealthy", {
              required: true,
              validate: (val: number) => {
                if (val < 1) {
                  return "Must be greater than or equal to 1";
                }
                return true;
              },
            })}
            inputType="number"
          />
        </div>
      </div>

      <div className="mt-4 border-t border-gray-300">
        <div className="grid grid-cols-3 gap-4 mt-9">
          <div className="flex justify-end items-center text-sm text-gray-900 font-medium">Alert me when the</div>
          <div className="gap-4 flex flex-row items-center col-span-2">
            <div className="flex flex-1">
              {metricAggregationFunction === "PERCENTILE" && (
                <>
                  <div className=" text-black">
                    <InputCreatableSelect
                      placeholder="50th"
                      options={defaultMetricPercentiles.map((percentile) => ({ label: `${percentile}th`, value: percentile }))}
                      control={control}
                      required={true}
                      errors={errors}
                      validate={undefined}
                      clearable={false}
                      name="metricPercentile"
                      sanitizer={(value) => {
                        return value && typeof value === "string" ? Number(value.replaceAll("th", "")) : value;
                      }}
                      labeler={(value) => {
                        return `${value}th`;
                      }}
                    />
                  </div>
                </>
              )}
              <div className="flex-1">
                <InputSearchableSelect
                  placeholder="sum"
                  options={Object.keys(MetricAggregationFunction).map((key) => ({ label: `${key}`.toLowerCase(), value: key }))}
                  control={control}
                  required={true}
                  errors={errors}
                  validate={undefined}
                  clearable={false}
                  name="metricAggregationFunction"
                ></InputSearchableSelect>
              </div>
            </div>
            <div>of</div>
            <div className="flex-1">
              <InputCreatableSelect
                placeholder="e.g. column_name"
                noOptionsMessage="No columns found. Please enter a column name."
                options={columnNames.map((columnName) => ({ value: columnName, label: columnName }))}
                control={control}
                required={true}
                errors={errors}
                validate={undefined}
                clearable={false}
                name="metricValueColumn"
              />
            </div>
          </div>

          <div className="flex justify-end items-center text-sm text-gray-900 font-medium">is</div>
          <div className="col-span-2 flex flex-row gap-4">
            <div className="flex-1">
              <InputSearchableSelect
                placeholder="greater than"
                options={Object.keys(MetricComparisonOperator).map((key) => ({
                  label: MetricComparisonOperatorToString[key as MetricComparisonOperator],
                  value: key,
                }))}
                control={control}
                required={false}
                errors={errors}
                validate={undefined}
                clearable={false}
                name="metricComparisonOperator"
              />
            </div>
            <div className="flex-1">
              <InputField
                hideErrorMessage
                errors={errors}
                placeholder="e.g. 100"
                register={register("metricThreshold", { required: true, validate: (val) => !isNaN(Number(val)) })}
              />
            </div>
          </div>
          <div className="flex justify-end items-center text-sm text-gray-900 font-medium">over</div>
          <div className="col-span-2 gap-4 flex flex-row">
            <div className="flex-1">
              <InputField
                hideErrorMessage
                errors={errors}
                defaultValue={5}
                placeholder="e.g. 5"
                inputType="number"
                register={register("metricLookbackValue", {
                  required: true,
                  valueAsNumber: true,
                  validate: (val: number) => {
                    if (val < 0) {
                      return "Lookback must be greater than or equal to 0";
                    }
                    return true;
                  },
                })}
              />
            </div>
            <div className="flex-1">
              <InputSearchableSelect
                placeholder="e.g. minutes"
                options={Object.keys(TimeUnit).map((key) => ({ label: `${key}`.toLowerCase(), value: key }))}
                control={control}
                required={true}
                errors={errors}
                validate={undefined}
                clearable={false}
                name="metricLookbackUnit"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-9 pt-9 border-t border-gray-300">
        <div className="grid grid-cols-3 gap-4 items-center">
          <div className="col-span-2 text-gray-900 font-medium text-sm justify-end flex">UTC timestamp for this metric</div>
          <div>
            <InputCreatableSelect
              placeholder={"e.g. created_at"}
              noOptionsMessage="No columns found. Please enter a column name."
              options={columnNames.map((columnName) => ({ value: columnName, label: columnName }))}
              control={control}
              required={true}
              errors={errors}
              validate={undefined}
              clearable={false}
              name="metricTimestampColumn"
            />
          </div>
        </div>
      </div>
    </>
  );
}
